import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import cookies from "js-cookie";
import i18next from "i18next";
import useAuth from "../hooks/useAuth";
import { useNavigationItems } from "../hooks/useNavigationItems";
import { cn } from "../lib/utils";
import { useDirection } from "../hooks/useDirection";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "./ui/sheet";
import {
  MessageSquareText,
  Network,
  Users,
  BookOpen,
  GraduationCap,
  Settings,
  Globe,
  Mail,
  LogOut,
  ChevronRight,
  Sparkles,
  LibraryBig,
  Store,
  Route,
  FlaskConical,
} from "lucide-react";
import { useAiCredits, openBuyAiCredits } from "./pwf/studio/AiCredits";

interface MobileNavDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLogout: () => void;
}

const MobileNavDrawer: React.FC<MobileNavDrawerProps> = ({
  open,
  onOpenChange,
  onLogout,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isRTL } = useDirection();
  const { unreadChatsCount, navigateToPortalTab, isMentor, isVendor, showGroups } = useNavigationItems();

  const displayName =
    user?.displayName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.email?.split("@")[0] ||
    "";

  const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(
    displayName || "U"
  )}&background=e8edf5&color=4a6fa5&bold=true&size=48`;

  const goTo = (path: string) => {
    navigate(path);
    onOpenChange(false);
  };

  const handlePortalTab = (tab: string) => {
    navigateToPortalTab(tab);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={isRTL ? "right" : "left"} className="w-[280px] p-0 flex flex-col">
        <SheetTitle className="sr-only">{t("navigation", "Navigation")}</SheetTitle>

        {/* Profile section */}
        <div className="px-5 pt-12 pb-4">
          <button
            onClick={() => goTo("/profile")}
            className="flex items-center gap-3 w-full text-start"
          >
            <img
              src={avatarUrl}
              alt=""
              className="w-10 h-10 rounded-full ring-2 ring-gray-100"
            />
            <div className="flex-1 min-w-0">
              {displayName && (
                <p className="text-sm font-semibold text-foreground truncate">
                  {displayName}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {t("view_profile", "View Profile")}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground/40 shrink-0" />
          </button>
        </div>

        <div className="h-px bg-gray-200/80 mx-4" />

        {/* Menu items */}
        <div className="flex-1 overflow-y-auto py-2">
          <DrawerItem
            icon={Route}
            label={t("nav_studio", "Studio")}
            onClick={() => goTo("/pwf")}
          />
          <DrawerItem
            icon={MessageSquareText}
            label={t("chats")}
            badge={unreadChatsCount}
            onClick={() => goTo("/chats")}
          />

          {isMentor && (
            <DrawerItem
              icon={GraduationCap}
              label={t("mentoring") || "Mentoring"}
              onClick={() => goTo("/mentoring")}
            />
          )}
          {isMentor && (
            <DrawerItem
              icon={Users}
              label={t("my_mentees")}
              onClick={() => handlePortalTab("my_mentees")}
            />
          )}
          {showGroups && (
            <DrawerItem
              icon={Network}
              label={t("groups")}
              onClick={() => handlePortalTab("my_groups")}
            />
          )}
          {isVendor && (
            <DrawerItem
              icon={Store}
              label={t("nav_store", "Store")}
              onClick={() => goTo("/commerce")}
            />
          )}
          <DrawerItem
            icon={LibraryBig}
            label={t("marketplace")}
            onClick={() => goTo("/marketplace")}
          />
          <DrawerItem
            icon={FlaskConical}
            label={t("science")}
            onClick={() => goTo("/science")}
          />
          <DrawerItem
              icon={GraduationCap}
              label={t("home_tab_learn", "Learn")}
              onClick={() => {
                window.dispatchEvent(
                    new CustomEvent("home-tab-change", { detail: "learn" })
                );
                goTo("/home");
              }}
          />
          <DrawerItem
            icon={BookOpen}
            label={t("examples")}
            onClick={() => goTo("/examples")}
          />

          <div className="h-px bg-gray-200/80 mx-4 my-2" />

          <DrawerItem
            icon={Mail}
            label={t("contact_us")}
            onClick={() => goTo("/contact")}
          />
          <DrawerItem
            icon={Settings}
            label={t("my_account")}
            onClick={() => goTo("/account")}
          />

          <AiCreditsDrawerItem
            onOpen={() => {
              onOpenChange(false);
              openBuyAiCredits();
            }}
          />

          <LanguageDrawerItem />

          <div className="h-px bg-gray-200/80 mx-4 my-2" />

          <DrawerItem
            icon={LogOut}
            label={t("logout")}
            onClick={() => {
              onOpenChange(false);
              onLogout();
            }}
            variant="destructive"
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ─── AI credits row ──────────────────────────────────────────────────────────
// Shows the live balance; tapping opens the buy-credits dialog (host is
// mounted by the Header, which also renders this drawer).

const AiCreditsDrawerItem: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { t } = useTranslation();
  const credits = useAiCredits();
  return (
    <button
      onClick={onOpen}
      className="flex items-center gap-3 w-full px-5 py-3 text-start transition-colors min-h-[48px] text-foreground hover:bg-muted/50"
    >
      <Sparkles className="w-5 h-5 shrink-0 opacity-70" />
      <span className="text-sm font-medium flex-1">{t("ai_credits", "AI credits")}</span>
      <span className="rounded-full bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5">
        {credits ?? "…"}
      </span>
    </button>
  );
};

// ─── Drawer Item ─────────────────────────────────────────────────────────────

interface DrawerItemProps {
  icon: React.ElementType;
  label: string;
  badge?: number;
  onClick: () => void;
  variant?: "default" | "destructive";
}

const DrawerItem: React.FC<DrawerItemProps> = ({
  icon: Icon,
  label,
  badge,
  onClick,
  variant = "default",
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 w-full px-5 py-3 text-start transition-colors min-h-[48px]",
        variant === "destructive"
          ? "text-red-600 hover:bg-red-50"
          : "text-foreground hover:bg-muted/50",
      )}
    >
      <Icon className="w-5 h-5 shrink-0 opacity-70" />
      <span className="text-sm font-medium flex-1">{label}</span>
      {badge != null && badge > 0 && (
        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
};

// ─── Language Selector ───────────────────────────────────────────────────────

const availableLanguages = [
  { code: "he", name: "עברית", country_code: "il", dir: "rtl" },
  { code: "en", name: "English", country_code: "gb" },
  { code: "de", name: "Deutsch", country_code: "de" },
];

const LanguageDrawerItem: React.FC = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  const currentLang = cookies.get("i18next")?.split("-")[0] || "en";

  const selectLanguage = (code: string) => {
    i18next.changeLanguage(code);
    setExpanded(false);
  };

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-3 w-full px-5 py-3 text-start transition-colors min-h-[48px] text-foreground hover:bg-muted/50"
      >
        <Globe className="w-5 h-5 shrink-0 opacity-70" />
        <span className="text-sm font-medium flex-1">
          {t("language", "Language")}
        </span>
        <ChevronRight
          className={cn(
            "w-4 h-4 text-muted-foreground/40 shrink-0 transition-transform duration-200",
            expanded && "rotate-90",
          )}
        />
      </button>
      {expanded && (
        <div className="ps-12 pe-4 pb-1">
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => selectLanguage(lang.code)}
              className={cn(
                "flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm transition-colors",
                currentLang === lang.code
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-foreground hover:bg-muted/50",
              )}
            >
              <span className={`flag-icon flag-icon-${lang.country_code}`} />
              {lang.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileNavDrawer;
